import React from 'react';
import { I18n } from 'react-redux-i18n';

import { I18N_BASE } from '../../constants';
import {
  QueueFilters,
  QueueSortKey,
  QueueSubjectFilter,
} from '../../models';
import {
  filtersForQueueView,
  QueueView,
  QUEUE_VIEWS,
  queueViewFromFilters,
} from '../../helpers/queueView';
import {
  rangeForScoreBand,
  ScoreBand,
  SCORE_BANDS,
  scoreBandFromRange,
} from '../../helpers/scoreBand';
import CmDropdown from '../../components/primitives/CmDropdown';
import styles from './queue.module.css';

export interface QueueFiltersBarProps {
  value: QueueFilters;
  onChange: (next: QueueFilters) => void;
  testId?: string;
}

function pillClass(active: boolean): string {
  return `${styles.pill} ${active ? styles.pillActive : ''}`.trim();
}

const SUBJECT_OPTIONS: readonly QueueSubjectFilter[] = ['pages', 'blobs'];

const SORT_OPTIONS: readonly QueueSortKey[] = ['scoreDesc', 'ageDesc', 'ageAsc'];

export function QueueFiltersBar({
  value,
  onChange,
  testId,
}: QueueFiltersBarProps): React.ReactElement {
  const setView = (view: QueueView) => {
    const next = filtersForQueueView(view);
    onChange({ ...value, ...next, page: 1 });
  };

  const setSubjectFilter = (subjectFilter: QueueSubjectFilter) =>
    onChange({ ...value, subjectFilter, page: 1 });

  const setScoreBand = (band: ScoreBand) => {
    const range = rangeForScoreBand(band);
    onChange({ ...value, minScore: range.min, maxScore: range.max, page: 1 });
  };

  const currentView = queueViewFromFilters(value.band, value.decisionState);
  const currentScoreBand = scoreBandFromRange(value.minScore, value.maxScore);

  return (
    <div className={styles.filtersBar} data-testid={testId}>
      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>
          {I18n.t(`${I18N_BASE}.queue.filters.view`)}
        </span>
        {QUEUE_VIEWS.map((view) => (
          <button
            key={view}
            type="button"
            className={pillClass(currentView === view)}
            // aria-pressed exposes the toggle state to screen readers; without
            // it the only "selected" signal is the visual pillActive class.
            aria-pressed={currentView === view}
            onClick={() => setView(view)}
            data-testid={`filter-view-${view}`}
          >
            {I18n.t(`${I18N_BASE}.queue.filters.viewOption.${view}`)}
          </button>
        ))}
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>
          {I18n.t(`${I18N_BASE}.queue.filters.subject`)}
        </span>
        {SUBJECT_OPTIONS.map((subject) => (
          <button
            key={subject}
            type="button"
            className={pillClass(value.subjectFilter === subject)}
            aria-pressed={value.subjectFilter === subject}
            onClick={() => setSubjectFilter(subject)}
            data-testid={`filter-subject-${subject}`}
          >
            {I18n.t(`${I18N_BASE}.queue.filters.subjectOption.${subject}`)}
          </button>
        ))}
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>
          {I18n.t(`${I18N_BASE}.queue.filters.score`)}
        </span>
        <CmDropdown
          value={currentScoreBand}
          options={SCORE_BANDS.map((b) => ({
            value: b.band,
            text:
              b.band === 'all'
                ? I18n.t(`${I18N_BASE}.queue.filters.scoreAll`)
                : `${b.min}–${b.max}`,
          }))}
          onChange={(band) => setScoreBand(band)}
          testId="filter-score-band"
          ariaLabel={I18n.t(`${I18N_BASE}.queue.filters.score`)}
        />
      </div>

      <div className={styles.filterGroup}>
        <span className={styles.filterLabel}>
          {I18n.t(`${I18N_BASE}.queue.filters.sort`)}
        </span>
        <CmDropdown
          value={value.sort}
          options={SORT_OPTIONS.map((s) => ({
            value: s,
            text: I18n.t(`${I18N_BASE}.queue.filters.sortOption.${s}`),
          }))}
          onChange={(sort) => onChange({ ...value, sort })}
          testId="filter-sort"
        />
      </div>
    </div>
  );
}

export default QueueFiltersBar;
